/**
 * SiteNavTree — recursive page-tree renderer.
 *
 * Sprint CM-11 TKT-CM11-1. Used in the sidebar (each Intranet Site
 * expands to its full page hierarchy) and reused inside the
 * LinkPickerModal's Internal-page tab (TKT-CM11-2).
 *
 * Pages are fetched once per site via GET /mod/cm/pages?site_id=X
 * and cached at the component instance. Slug paths are built from
 * each page's parent_page_id chain so we can address pages via the
 * existing /modules/cm/<site-slug>/<a>/<b>/<c> catch-all route.
 *
 * Cross-References:
 *   - Sprint CM-11 (sprint-cm-11-nav-tree-link-picker.md)
 *   - Existing PageView resolves via /pages/by-path
 */
import { NavLink } from 'react-router-dom';
import { ChevronDown, ChevronRight, ExternalLink, FileText } from 'lucide-react';
import { useEffect, useState, useCallback, type FC } from 'react';
import { apiFetch } from '@/lib/api';
import ExternalSiteIcon from '@/components/shell/ExternalSiteIcon';

export interface PageNode {
  id: string;
  title: string;
  slug: string;
  parent_page_id: string | null;
  status: string;
  // 2026-05-22 — child pages can also be external-link rows
  // (e.g. an external page demoted from root via the gear-modal Move
  // action). Without this the tree fell back to a NavLink that resolved
  // to an empty internal page. Backend PageResponse already includes
  // external_url + external_favicon_url; we just hadn't surfaced them
  // in the tree node type.
  external_url?: string | null;
  external_favicon_url?: string | null;
}

export interface SiteNavTreeProps {
  siteSlug: string;
  siteId: string;
  /** When set, rows render as buttons instead of NavLinks and call
   *  onPick when clicked (used by LinkPickerModal). */
  onPick?: (page: PageNode, slugPath: string) => void;
  /** Hide draft pages — sidebar shows everything authors can see;
   *  picker may want only published. Defaults to false. */
  publishedOnly?: boolean;
  /** Fetched pages can be passed in by the parent so multiple
   *  trees in the same view share one fetch. Optional. */
  pages?: PageNode[] | null;
  /** Indent depth, used recursively. */
  depth?: number;
  /** Sprint CM-11.1 — when the landing page has no children, hide it
   *  from the tree (the site name already navigates there). When it
   *  has children, render it as the top-level node so the sub-pages
   *  appear nested under it. */
  landingPageId?: string | null;
}

/* ── Slug-path helpers ────────────────────────────────────── */

/** Build a {pageId → slugPath} map by walking parent chains. */
function buildSlugPaths(pages: PageNode[]): Map<string, string> {
  const byId = new Map(pages.map((p) => [p.id, p]));
  const out = new Map<string, string>();
  const resolve = (p: PageNode): string => {
    if (out.has(p.id)) return out.get(p.id)!;
    const segments: string[] = [];
    let cur: PageNode | undefined = p;
    let safety = 8;
    while (cur && safety > 0) {
      segments.unshift(cur.slug);
      cur = cur.parent_page_id ? byId.get(cur.parent_page_id) : undefined;
      safety -= 1;
    }
    const path = segments.join('/');
    out.set(p.id, path);
    return path;
  };
  for (const p of pages) resolve(p);
  return out;
}

/* ── Tree renderer ────────────────────────────────────────── */

const STORAGE_KEY = 'cm-sidebar-page-expanded';

function readExpanded(): Set<string> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? new Set(arr) : new Set();
  } catch {
    return new Set();
  }
}

function writeExpanded(expanded: Set<string>) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
  } catch {
    /* localStorage unavailable */
  }
}

const SiteNavTree: FC<SiteNavTreeProps> = ({
  siteSlug,
  siteId,
  onPick,
  publishedOnly = false,
  pages: externalPages,
  depth = 0,
  landingPageId = null,
}) => {
  const [pages, setPages] = useState<PageNode[] | null>(externalPages ?? null);
  const [loading, setLoading] = useState(false);
  const [expandedPages, setExpandedPages] = useState<Set<string>>(() =>
    readExpanded(),
  );

  const fetchPages = useCallback(async () => {
    if (pages !== null || externalPages) return;
    setLoading(true);
    try {
      // CM-17 — the `siteId` prop is the root page id post-cutover;
      // backend matches against Page.root_page_id rather than the
      // deprecated Page.site_id (which only survives via legacy_site_id
      // on backfilled rows).
      // CW-IS-W2 — exclude_self=true keeps the root page out of its own
      // subtree; the parent component renders it as the section header
      // NavLink.
      const data = await apiFetch<PageNode[]>(`/mod/cm/pages?root_page_id=${siteId}&exclude_self=true`);
      setPages(data || []);
    } catch {
      setPages([]);
    } finally {
      setLoading(false);
    }
  }, [siteId, pages, externalPages]);

  useEffect(() => {
    if (externalPages) {
      setPages(externalPages);
      return;
    }
    void fetchPages();
  }, [externalPages, fetchPages]);

  const togglePage = (id: string) => {
    setExpandedPages((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      writeExpanded(next);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="sidebar__tree-loading">Loading…</div>
    );
  }
  if (!pages || pages.length === 0) {
    return null;
  }

  const filtered = publishedOnly
    ? pages.filter((p) => p.status === 'published')
    : pages;
  const slugPaths = buildSlugPaths(filtered);

  // Group by parent. parent_page_id == null → top-level.
  const childrenByParent = new Map<string | null, PageNode[]>();
  for (const p of filtered) {
    const key = p.parent_page_id ?? null;
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key)!.push(p);
  }
  for (const arr of childrenByParent.values()) {
    arr.sort((a, b) => a.title.localeCompare(b.title));
  }

  const renderNode = (page: PageNode, level: number): JSX.Element => {
    const kids = childrenByParent.get(page.id) ?? [];
    const hasKids = kids.length > 0;
    const isExpanded = expandedPages.has(page.id);
    const slugPath = slugPaths.get(page.id) ?? page.slug;
    const url = `/modules/cm/${siteSlug}/${slugPath}`;

    const isExternal = Boolean(page.external_url);
    const RowIcon = isExternal
      ? (
        <ExternalSiteIcon
          faviconUrl={page.external_favicon_url}
          size={14}
          className="sidebar__tree-icon"
        />
      )
      : <FileText size={14} className="sidebar__tree-icon" />;

    const rowContent = (
      <>
        <span
          className="sidebar__tree-row"
          style={{ paddingLeft: `${level * 12}px` }}
        >
          {hasKids ? (
            <button
              className="sidebar__tree-toggle"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                togglePage(page.id);
              }}
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronDown size={14} />
              ) : (
                <ChevronRight size={14} />
              )}
            </button>
          ) : (
            <span className="sidebar__tree-spacer" />
          )}
          {RowIcon}
          <span className="sidebar__tree-title">{page.title}</span>
          {isExternal && !onPick && (
            <ExternalLink size={12} style={{ marginLeft: 'auto', opacity: 0.6 }} />
          )}
        </span>
      </>
    );

    // Sprint CM-12 parity for child rows — an external-link page
    // (regardless of whether it's a root or sits under another root)
    // renders as a target=_blank anchor. Without this branch the
    // internal NavLink dropped the user on a content-less page.
    if (isExternal && !onPick && page.external_url) {
      return (
        <div key={page.id}>
          <a
            href={page.external_url}
            target="_blank"
            rel="noopener noreferrer"
            className="sidebar__link"
            title={page.external_url}
          >
            {rowContent}
          </a>
          {hasKids && isExpanded && (
            <div>{kids.map((k) => renderNode(k, level + 1))}</div>
          )}
        </div>
      );
    }

    return (
      <div key={page.id}>
        {onPick ? (
          <button
            type="button"
            className="sidebar__link sidebar__tree-button"
            onClick={() => onPick(page, slugPath)}
          >
            {rowContent}
          </button>
        ) : (
          <NavLink
            to={url}
            className={({ isActive }) =>
              `sidebar__link${isActive ? ' sidebar__link--active' : ''}`
            }
          >
            {rowContent}
          </NavLink>
        )}
        {hasKids && isExpanded && (
          <div>
            {kids.map((k) => renderNode(k, level + 1))}
          </div>
        )}
      </div>
    );
  };

  let tops = childrenByParent.get(null) ?? [];

  // CW-IS-W2 TKT-4 — Post-CM-17, the root page IS the landing page and
  // the parent component (Sidebar) already renders it as a section
  // NavLink. The tree must show ONLY the root's descendants. Lift the
  // root's children up to top-level and never render the root row here.
  // Belt-and-braces alongside the backend `exclude_self=true` filter
  // and the cm0020 self-loop migration.
  if (landingPageId) {
    const landingKids = childrenByParent.get(landingPageId) ?? [];
    const others = tops.filter((p) => p.id !== landingPageId);
    tops = [...landingKids, ...others];
  }

  return <>{tops.map((p) => renderNode(p, depth))}</>;
};

export default SiteNavTree;
